// ────────────────────────────────────────────────────────────────────
// Dock window registry — the single source of truth for every window
// that can live in the bottom dock (MODS, customer chat, ...). A window
// is "registered" once (by whoever mounts it) and re-registered on every
// render after that so its title/icon/body always reflect the latest
// props; registerWindow is idempotent for that reason — it must NOT
// reset open/minimized on an already-known id, or a live conversation
// would collapse every time its own state changed.
//
// Stacking order ("newest nearest the bottom-right corner, older to the
// left") is tracked via a monotonic `openedAt` counter bumped whenever a
// window is opened/restored/brought-to-front, and `windows` is exposed
// pre-sorted ascending by that counter — DockBar can render it directly
// with a right-aligned row and the newest naturally lands rightmost.
// ────────────────────────────────────────────────────────────────────
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "xzynexa.dock";

export interface DockWindowSpec {
  title: string;
  icon?: ReactNode;
  body: ReactNode;
  /** Only consulted the FIRST time this id is registered (and only when
   *  nothing was persisted for it) — later re-registrations never reopen
   *  or reclose a window the user already acted on. */
  defaultOpen?: boolean;
  /** Drives the unread dot on the minimized pill; caller recomputes this
   *  each render (e.g. from a poll), same pattern as TeamChatDock's own
   *  `unread` today. */
  unread?: boolean;
  /** Optional small node rendered right after the title in the window
   *  header (e.g. MODS's PREMIUM badge). Not shown on pills. */
  badge?: ReactNode;
}

export interface DockWindowState {
  id: string;
  title: string;
  icon?: ReactNode;
  body: ReactNode;
  unread?: boolean;
  badge?: ReactNode;
  open: boolean;
  minimized: boolean;
}

interface DockContextValue {
  /** Ordered oldest→newest; render right-aligned so the newest lands at
   *  the corner. */
  windows: DockWindowState[];
  registerWindow: (id: string, spec: DockWindowSpec) => void;
  unregisterWindow: (id: string) => void;
  openWindow: (id: string) => void;
  closeWindow: (id: string) => void;
  minimize: (id: string) => void;
  restore: (id: string) => void;
  toggle: (id: string) => void;
  isOpen: (id: string) => boolean;
  isMinimized: (id: string) => boolean;
}

interface Persisted {
  open: boolean;
  minimized: boolean;
}

// Internal-only shape — `openedAt` never leaves the provider; it's stripped
// implicitly since consumers only see the `DockWindowState` fields they
// destructure.
interface InternalWindow extends DockWindowState {
  openedAt: number;
}

function loadPersisted(): Record<string, Persisted> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, Persisted>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    // localStorage unavailable (private mode, disabled, SSR) — start clean.
    return {};
  }
}

function savePersisted(windows: InternalWindow[]): void {
  try {
    const data: Record<string, Persisted> = {};
    for (const w of windows) data[w.id] = { open: w.open, minimized: w.minimized };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable — nothing to persist, refresh just won't
    // restore state. Not fatal.
  }
}

const DockContext = createContext<DockContextValue | null>(null);

export function DockProvider({ children }: { children: ReactNode }): JSX.Element {
  // Loaded once per provider instance (i.e. once per mount) — a fresh
  // mount is exactly the "refresh" case the persistence is for.
  const persistedRef = useRef<Record<string, Persisted>>(loadPersisted());
  const orderRef = useRef(0);
  const [windows, setWindows] = useState<InternalWindow[]>([]);

  const registerWindow = useCallback((id: string, spec: DockWindowSpec) => {
    setWindows((prev) => {
      const idx = prev.findIndex((w) => w.id === id);
      if (idx >= 0) {
        // Update the live parts only; open/minimized/openedAt are owned by
        // the user's actions, not by whatever re-registered this render.
        const existing = prev[idx];
        if (
          existing.title === spec.title &&
          existing.icon === spec.icon &&
          existing.body === spec.body &&
          existing.unread === spec.unread
        ) {
          return prev; // no-op update — skip the re-render
        }
        const next = prev.slice();
        next[idx] = { ...existing, title: spec.title, icon: spec.icon, body: spec.body, unread: spec.unread, badge: spec.badge };
        return next;
      }
      const persisted = persistedRef.current[id];
      const open = persisted ? persisted.open : !!spec.defaultOpen;
      const minimized = persisted ? persisted.minimized : false;
      const openedAt = open ? orderRef.current++ : -1;
      const record: InternalWindow = {
        id, title: spec.title, icon: spec.icon, body: spec.body, unread: spec.unread, badge: spec.badge,
        open, minimized, openedAt,
      };
      return [...prev, record];
    });
  }, []);

  const unregisterWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const openWindow = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, open: true, minimized: false, openedAt: orderRef.current++ } : w
    )));
  }, []);

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, open: false, minimized: false } : w
    )));
  }, []);

  const minimize = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  // Also used to "bring to front" an already-open, non-minimized window
  // (the narrow-viewport switcher does this) — restoring and front-ing are
  // the same operation: minimized=false + newest openedAt.
  const restore = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => (
      w.id === id ? { ...w, open: true, minimized: false, openedAt: orderRef.current++ } : w
    )));
  }, []);

  const toggle = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => {
      if (w.id !== id) return w;
      if (w.open && !w.minimized) return { ...w, open: false, minimized: false };
      return { ...w, open: true, minimized: false, openedAt: orderRef.current++ };
    }));
  }, []);

  const isOpen = useCallback((id: string) => windows.some((w) => w.id === id && w.open), [windows]);
  const isMinimized = useCallback((id: string) => windows.some((w) => w.id === id && w.minimized), [windows]);

  // Persist ONLY open/minimized booleans, never the body (React nodes,
  // functions, etc. — not serializable and not meant to survive a refresh
  // anyway; the next mount re-registers with live content).
  useEffect(() => { savePersisted(windows); }, [windows]);

  const orderedWindows = useMemo(
    () => windows.slice().sort((a, b) => a.openedAt - b.openedAt),
    [windows],
  );

  const value = useMemo<DockContextValue>(() => ({
    windows: orderedWindows,
    registerWindow, unregisterWindow, openWindow, closeWindow, minimize, restore, toggle,
    isOpen, isMinimized,
  }), [orderedWindows, registerWindow, unregisterWindow, openWindow, closeWindow, minimize, restore, toggle, isOpen, isMinimized]);

  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
}

export function useDock(): DockContextValue {
  const ctx = useContext(DockContext);
  if (!ctx) throw new Error("useDock must be used within a DockProvider");
  return ctx;
}
